import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { Navigate, Link } from "react-router-dom";
import { LogOut, Loader2, Plus, Trash2, Save, Edit3, Images } from "lucide-react";
import { useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import ImageUploadField from "@/components/ImageUploadField";

const Section = ({ title, children, action }) => (
    <section className="glass-card rounded-xl p-6 md:p-8 mb-6">
        <div className="flex justify-between items-center mb-5">
            <h2 className="font-serif text-xl md:text-2xl text-white">{title}</h2>
            {action}
        </div>
        {children}
    </section>
);

const Field = ({ label, value, onChange, multiline, type = "text" }) => (
    <label className="block mb-3">
        <div className="text-[11px] uppercase tracking-[0.18em] text-white/60 mb-1.5">{label}</div>
        {multiline ? (
            <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3}
                className="w-full bg-bg-base border border-white/15 rounded-lg px-3 py-2 text-white text-sm focus:border-gold focus:outline-none" />
        ) : (
            <input type={type} value={value ?? ""} onChange={(e) => onChange(type === "number" ? Number(e.target.value) : e.target.value)}
                className="w-full bg-bg-base border border-white/15 rounded-lg px-3 py-2 text-white text-sm focus:border-gold focus:outline-none" />
        )}
    </label>
);

const AdminDashboard = () => {
    const { user, loading, logout } = useAuth();
    const { settings, refresh, setSettings } = useSettings();
    const [tab, setTab] = useState("site");
    const [services, setServices] = useState([]);
    const [gallery, setGallery] = useState([]);
    const [reviews, setReviews] = useState([]);
    const [enquiries, setEnquiries] = useState([]);
    const [editing, setEditing] = useState(null); // {type, item}
    const [saving, setSaving] = useState(false);
    const [bulkCategory, setBulkCategory] = useState("Wedding");
    const [bulkUploading, setBulkUploading] = useState(false);
    const bulkInputRef = useRef(null);

    const refreshAll = async () => {
        const [s, g, r, e] = await Promise.all([
            api.get("/services"), api.get("/gallery"), api.get("/reviews"),
            api.get("/admin/enquiries").catch(() => ({ data: [] })),
        ]);
        setServices(s.data); setGallery(g.data); setReviews(r.data); setEnquiries(e.data);
    };

    useEffect(() => { if (user) refreshAll(); }, [user]);

    if (loading) return <div className="min-h-screen grid place-items-center text-gold"><Loader2 className="animate-spin" /></div>;
    if (!user) return <Navigate to="/admin/login" replace />;

    const saveSettings = async () => {
        setSaving(true);
        try {
            const { data } = await api.put("/admin/settings", settings);
            setSettings(data);
            await refresh();
            toast.success("Settings saved");
        } catch { toast.error("Save failed"); }
        finally { setSaving(false); }
    };

    const updateSetting = (k, v) => setSettings({ ...settings, [k]: v });

    const handleBulkUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setBulkUploading(true);
        try {
            const fd = new FormData();
            fd.append("category", bulkCategory);
            files.forEach((f) => fd.append("files", f));
            const { data } = await api.post("/admin/gallery/bulk", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(`${data.created} image(s) added to ${bulkCategory}`);
            await refreshAll();
        } catch {
            toast.error("Bulk upload failed");
        } finally {
            setBulkUploading(false);
            if (bulkInputRef.current) bulkInputRef.current.value = "";
        }
    };

    // CRUD helpers
    const saveItem = async (type, item) => {
        try {
            if (item.id) {
                await api.put(`/admin/${type}/${item.id}`, item);
            } else {
                await api.post(`/admin/${type}`, item);
            }
            toast.success("Saved");
            setEditing(null);
            await refreshAll();
        } catch { toast.error("Save failed"); }
    };

    const deleteItem = async (type, id) => {
        if (!window.confirm("Delete this item?")) return;
        try {
            await api.delete(`/admin/${type}/${id}`);
            toast.success("Deleted");
            await refreshAll();
        } catch { toast.error("Delete failed"); }
    };

    return (
        <div className="min-h-screen bg-bg-base text-white">
            {/* Top bar */}
            <div className="border-b border-white/10 bg-black/70 backdrop-blur sticky top-0 z-30">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <span className="text-2xl gold-text-gradient font-serif font-bold">★</span>
                        <div className="font-serif text-lg text-white">Star Events Admin</div>
                    </Link>
                    <div className="flex items-center gap-4 text-sm">
                        <Link to="/" className="text-white/70 hover:text-gold">View Site</Link>
                        <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 text-white/70 hover:text-gold">
                            <LogOut size={16} /> Logout
                        </button>
                    </div>
                </div>
                <div className="max-w-7xl mx-auto px-6 flex gap-1 overflow-x-auto">
                    {["site", "services", "gallery", "reviews", "enquiries"].map(t => (
                        <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`}
                            className={`px-4 py-3 text-sm uppercase tracking-wider transition-colors ${tab === t ? "text-gold border-b-2 border-gold" : "text-white/60 hover:text-white"}`}>
                            {t}
                        </button>
                    ))}
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 py-10">
                {/* SITE settings */}
                {tab === "site" && settings && (
                    <>
                        <Section title="Branding & SEO" action={<button onClick={saveSettings} disabled={saving} data-testid="save-settings" className="btn-gold text-sm inline-flex items-center gap-2"><Save size={14} />{saving ? "Saving..." : "Save All"}</button>}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <Field label="Company Name" value={settings.company_name} onChange={v => updateSetting("company_name", v)} />
                                <Field label="Tagline" value={settings.tagline} onChange={v => updateSetting("tagline", v)} />
                                <ImageUploadField label="Logo" value={settings.logo_url} onChange={v => updateSetting("logo_url", v)} />
                                <ImageUploadField label="Favicon" value={settings.favicon_url} onChange={v => updateSetting("favicon_url", v)} aspect="aspect-square" />
                                <Field label="Meta Title" value={settings.meta_title} onChange={v => updateSetting("meta_title", v)} />
                                <Field label="Meta Description" value={settings.meta_description} onChange={v => updateSetting("meta_description", v)} multiline />
                            </div>
                        </Section>

                        <Section title="Hero Section">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <Field label="Hero Title" value={settings.hero_title} onChange={v => updateSetting("hero_title", v)} />
                                <Field label="Hero Subtitle" value={settings.hero_subtitle} onChange={v => updateSetting("hero_subtitle", v)} multiline />
                                <Field label="Hero Image URL" value={settings.hero_image_url} onChange={v => updateSetting("hero_image_url", v)} />
                                <Field label="Hero Video URL (optional)" value={settings.hero_video_url} onChange={v => updateSetting("hero_video_url", v)} />
                            </div>
                        </Section>

                        <Section title="About / Owner">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <Field label="Owner Name" value={settings.owner_name} onChange={v => updateSetting("owner_name", v)} />
                                <ImageUploadField label="Owner Photo" value={settings.owner_photo_url} onChange={v => updateSetting("owner_photo_url", v)} aspect="aspect-[3/4]" />
                                <Field label="Experience (years)" type="number" value={settings.experience_years} onChange={v => updateSetting("experience_years", v)} />
                                <Field label="Owner Bio" value={settings.owner_bio} onChange={v => updateSetting("owner_bio", v)} multiline />
                                <Field label="About Text" value={settings.about_text} onChange={v => updateSetting("about_text", v)} multiline />
                                <Field label="Mission" value={settings.mission} onChange={v => updateSetting("mission", v)} multiline />
                                <Field label="Vision" value={settings.vision} onChange={v => updateSetting("vision", v)} multiline />
                            </div>
                        </Section>

                        <Section title="Statistics">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <Field label="Events" type="number" value={settings.stats_events} onChange={v => updateSetting("stats_events", v)} />
                                <Field label="Clients" type="number" value={settings.stats_clients} onChange={v => updateSetting("stats_clients", v)} />
                                <Field label="Cities" type="number" value={settings.stats_cities} onChange={v => updateSetting("stats_cities", v)} />
                                <Field label="Years" type="number" value={settings.stats_years} onChange={v => updateSetting("stats_years", v)} />
                            </div>
                        </Section>

                        <Section title="Contact & Social">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <Field label="Phone" value={settings.phone} onChange={v => updateSetting("phone", v)} />
                                <Field label="WhatsApp" value={settings.whatsapp} onChange={v => updateSetting("whatsapp", v)} />
                                <Field label="Instagram URL" value={settings.instagram_url} onChange={v => updateSetting("instagram_url", v)} />
                                <Field label="Google Maps URL" value={settings.google_maps_url} onChange={v => updateSetting("google_maps_url", v)} />
                                <Field label="Google Maps Embed URL" value={settings.google_maps_embed} onChange={v => updateSetting("google_maps_embed", v)} />
                                <Field label="Business Hours" value={settings.business_hours} onChange={v => updateSetting("business_hours", v)} />
                                <Field label="Address" value={settings.address} onChange={v => updateSetting("address", v)} multiline />
                                <Field label="Footer About" value={settings.footer_about} onChange={v => updateSetting("footer_about", v)} multiline />
                            </div>
                            <div className="mt-4">
                                <button onClick={saveSettings} disabled={saving} className="btn-gold text-sm inline-flex items-center gap-2"><Save size={14} />{saving ? "Saving..." : "Save All Settings"}</button>
                            </div>
                        </Section>
                    </>
                )}

                {/* SERVICES */}
                {tab === "services" && (
                    <Section title="Services" action={
                        <button onClick={() => setEditing({ type: "services", item: { title: "", description: "", image_url: "", order: services.length } })} className="btn-gold text-sm inline-flex items-center gap-2"><Plus size={14} /> Add Service</button>
                    }>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {services.map(s => (
                                <div key={s.id} className="flex gap-4 border border-white/10 rounded-lg p-3">
                                    <img src={s.image_url} alt={s.title} className="w-20 h-20 object-cover rounded" />
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium text-white">{s.title}</div>
                                        <div className="text-xs text-white/55 line-clamp-2">{s.description}</div>
                                    </div>
                                    <div className="flex flex-col gap-2">
                                        <button onClick={() => setEditing({ type: "services", item: s })} className="text-gold hover:text-gold-hover"><Edit3 size={16} /></button>
                                        <button onClick={() => deleteItem("services", s.id)} className="text-red-400 hover:text-red-300"><Trash2 size={16} /></button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Section>
                )}

                {/* GALLERY */}
                {tab === "gallery" && (
                    <Section title="Gallery" action={
                        <button onClick={() => setEditing({ type: "gallery", item: { image_url: "", category: "Wedding", title: "" } })} className="btn-outline-gold text-sm inline-flex items-center gap-2"><Plus size={14} /> Add Single</button>
                    }>
                        {/* Bulk upload bar */}
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6 p-4 rounded-lg border border-gold/25 bg-gold/5">
                            <div className="flex items-center gap-2 text-gold shrink-0">
                                <Images size={18} /> <span className="text-sm font-semibold">Bulk Upload</span>
                            </div>
                            <select
                                value={bulkCategory}
                                onChange={(e) => setBulkCategory(e.target.value)}
                                data-testid="bulk-category-select"
                                className="bg-bg-base border border-white/15 rounded-lg px-3 py-2 text-white text-sm focus:border-gold focus:outline-none"
                            >
                                {["Wedding", "Birthday", "Corporate Events", "Smoke Effects", "Fireworks", "Stage Effects", "Event Setup"].map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                            <input
                                ref={bulkInputRef}
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={handleBulkUpload}
                                className="hidden"
                                data-testid="bulk-upload-input"
                            />
                            <button
                                onClick={() => bulkInputRef.current?.click()}
                                disabled={bulkUploading}
                                data-testid="bulk-upload-btn"
                                className="btn-gold text-sm inline-flex items-center justify-center gap-2"
                            >
                                {bulkUploading ? <><Loader2 size={14} className="animate-spin" /> Uploading…</> : <><Images size={14} /> Select multiple photos</>}
                            </button>
                            <span className="text-xs text-white/50">Selected photos are added to the chosen category.</span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {gallery.map(g => (
                                <div key={g.id} className="relative group rounded overflow-hidden">
                                    <img src={g.image_url} alt={g.title} className="w-full aspect-square object-cover" />
                                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity p-3 flex flex-col justify-between">
                                        <div className="text-xs text-gold">{g.category}</div>
                                        <button onClick={() => deleteItem("gallery", g.id)} className="self-end text-red-300 hover:text-red-200"><Trash2 size={16} /></button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Section>
                )}

                {/* REVIEWS */}
                {tab === "reviews" && (
                    <Section title="Reviews" action={
                        <button onClick={() => setEditing({ type: "reviews", item: { name: "", photo_url: "", rating: 5, review: "", event_type: "" } })} className="btn-gold text-sm inline-flex items-center gap-2"><Plus size={14} /> Add Review</button>
                    }>
                        <div className="space-y-3">
                            {reviews.map(r => (
                                <div key={r.id} className="border border-white/10 rounded-lg p-4 flex gap-4">
                                    {r.photo_url && <img src={r.photo_url} alt={r.name} className="w-12 h-12 rounded-full object-cover" />}
                                    <div className="flex-1 min-w-0">
                                        <div className="text-white font-medium">{r.name} <span className="text-xs text-gold ml-2">{r.event_type}</span></div>
                                        <div className="text-sm text-white/70 italic line-clamp-2">"{r.review}"</div>
                                    </div>
                                    <div className="flex flex-col gap-2">
                                        <button onClick={() => setEditing({ type: "reviews", item: r })} className="text-gold"><Edit3 size={16} /></button>
                                        <button onClick={() => deleteItem("reviews", r.id)} className="text-red-400"><Trash2 size={16} /></button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Section>
                )}

                {/* ENQUIRIES */}
                {tab === "enquiries" && (
                    <Section title="Enquiries">
                        {enquiries.length === 0 && <div className="text-white/50">No enquiries yet.</div>}
                        <div className="space-y-3">
                            {enquiries.map(e => (
                                <div key={e.id} className="border border-white/10 rounded-lg p-4">
                                    <div className="flex justify-between flex-wrap gap-2">
                                        <div className="text-white font-medium">{e.name} · <span className="text-gold">{e.phone}</span></div>
                                        <div className="text-xs text-white/50">{e.created_at}</div>
                                    </div>
                                    <div className="text-xs text-white/60 mt-1">{e.event_type} {e.email && `· ${e.email}`}</div>
                                    <div className="text-sm text-white/80 mt-2">{e.message}</div>
                                </div>
                            ))}
                        </div>
                    </Section>
                )}
            </div>

            {/* Edit modal */}
            {editing && (
                <div className="fixed inset-0 z-50 bg-black/80 grid place-items-center p-4" onClick={() => setEditing(null)}>
                    <div className="bg-bg-surface border border-white/10 rounded-xl p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
                        <h3 className="font-serif text-2xl text-white mb-5">{editing.item.id ? "Edit" : "Add"} {editing.type.slice(0, -1)}</h3>
                        {editing.type === "services" && (
                            <>
                                <Field label="Title" value={editing.item.title} onChange={v => setEditing({ ...editing, item: { ...editing.item, title: v } })} />
                                <Field label="Description" value={editing.item.description} onChange={v => setEditing({ ...editing, item: { ...editing.item, description: v } })} multiline />
                                <ImageUploadField label="Service Image" value={editing.item.image_url} onChange={v => setEditing({ ...editing, item: { ...editing.item, image_url: v } })} />
                                <Field label="Display Order" type="number" value={editing.item.order} onChange={v => setEditing({ ...editing, item: { ...editing.item, order: v } })} />
                            </>
                        )}
                        {editing.type === "gallery" && (
                            <>
                                <ImageUploadField label="Gallery Image" value={editing.item.image_url} onChange={v => setEditing({ ...editing, item: { ...editing.item, image_url: v } })} />
                                <label className="block mb-3">
                                    <div className="text-[11px] uppercase tracking-[0.18em] text-white/60 mb-1.5">Category</div>
                                    <select value={editing.item.category} onChange={(e) => setEditing({ ...editing, item: { ...editing.item, category: e.target.value } })}
                                        className="w-full bg-bg-base border border-white/15 rounded-lg px-3 py-2 text-white text-sm focus:border-gold focus:outline-none">
                                        {["Wedding", "Birthday", "Corporate Events", "Smoke Effects", "Fireworks", "Stage Effects", "Event Setup"].map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </label>
                                <Field label="Title (optional)" value={editing.item.title} onChange={v => setEditing({ ...editing, item: { ...editing.item, title: v } })} />
                            </>
                        )}
                        {editing.type === "reviews" && (
                            <>
                                <Field label="Name" value={editing.item.name} onChange={v => setEditing({ ...editing, item: { ...editing.item, name: v } })} />
                                <ImageUploadField label="Client Photo" value={editing.item.photo_url} onChange={v => setEditing({ ...editing, item: { ...editing.item, photo_url: v } })} aspect="aspect-square" />
                                <Field label="Rating (1-5)" type="number" value={editing.item.rating} onChange={v => setEditing({ ...editing, item: { ...editing.item, rating: v } })} />
                                <Field label="Review" value={editing.item.review} onChange={v => setEditing({ ...editing, item: { ...editing.item, review: v } })} multiline />
                                <Field label="Event Type" value={editing.item.event_type} onChange={v => setEditing({ ...editing, item: { ...editing.item, event_type: v } })} />
                            </>
                        )}
                        <div className="flex justify-end gap-3 mt-4">
                            <button onClick={() => setEditing(null)} className="btn-outline-gold text-sm">Cancel</button>
                            <button onClick={() => saveItem(editing.type, editing.item)} className="btn-gold text-sm">Save</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminDashboard;
